/**
 * Redux slice (src/store/slices/trialPreferencesSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { 
    Add_booking_Trial_Form, 
  Get_Trial_Booking,
  Cancel_Trial_Booking,
  Get_Student_Trial_Bookings,
  Update_Trial_Booking_Status,
  Get_Trial_Bookings_By_Package
} from "../actions/trialPreferencesActions";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  loading: false,
  error: null,
  currentBooking: null,
  studentBookings: [],
  packageBookings: [],
  success: false,
  count: 0 
};

/**
 * RTK slice definition: trialBookingSlice.
 */
const trialBookingSlice = createSlice({
  name: "trialBooking",
  initialState,
  reducers: {
    /**
     * Reducer: clearErrors.
     */
    clearErrors: (state) => {
      state.error = null;
    },
    /**
     * Reducer: resetSuccess.
     */
    resetSuccess: (state) => {
      state.success = false;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Add Trial Booking
      .addCase(Add_booking_Trial_Form.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Add_booking_Trial_Form.fulfilled
      .addCase(Add_booking_Trial_Form.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.data;
        state.success = true;
      })
      // extraReducers handler: Add_booking_Trial_Form.rejected
      .addCase(Add_booking_Trial_Form.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Trial Booking
      .addCase(Get_Trial_Booking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Get_Trial_Booking.fulfilled
      .addCase(Get_Trial_Booking.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.data;
      })
      // extraReducers handler: Get_Trial_Booking.rejected
      .addCase(Get_Trial_Booking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel Trial Booking
      .addCase(Cancel_Trial_Booking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Cancel_Trial_Booking.fulfilled
      .addCase(Cancel_Trial_Booking.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.data;
        state.success = true;
      })
      // extraReducers handler: Cancel_Trial_Booking.rejected
      .addCase(Cancel_Trial_Booking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Student Trial Bookings
      .addCase(Get_Student_Trial_Bookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Get_Student_Trial_Bookings.fulfilled
      .addCase(Get_Student_Trial_Bookings.fulfilled, (state, action) => {
        state.loading = false;
        state.studentBookings = action.payload.data;
      })
      // extraReducers handler: Get_Student_Trial_Bookings.rejected
      .addCase(Get_Student_Trial_Bookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Trial Booking Status
      .addCase(Update_Trial_Booking_Status.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Update_Trial_Booking_Status.fulfilled
      .addCase(Update_Trial_Booking_Status.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload.data;
        state.success = true;
      })
      // extraReducers handler: Update_Trial_Booking_Status.rejected
      .addCase(Update_Trial_Booking_Status.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // extraReducers handler: Get_Trial_Bookings_By_Package.pending
      .addCase(Get_Trial_Bookings_By_Package.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Get_Trial_Bookings_By_Package.fulfilled
      .addCase(Get_Trial_Bookings_By_Package.fulfilled, (state, action) => {
        state.loading = false;
        state.packageBookings = action.payload.data;
        state.count = action.payload.count;
      })
      // extraReducers handler: Get_Trial_Bookings_By_Package.rejected
      .addCase(Get_Trial_Bookings_By_Package.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, resetSuccess } = trialBookingSlice.actions;
export default trialBookingSlice.reducer;